import { PrimaryButton } from '../../../../../../components/execution/PrimaryButton'
import { EventNames } from '../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../utils/appBridge'
import { PlansLumpsumListEventIds, PlansLumpsumListScreenByPlanMode } from '../../../analytics/plansEvents'
import { PlansErrorStateLayout } from '../../../ui/PlansErrorStateLayout'
import { PlansPlanMode } from '../../api/types'

type PlansListErrorStatePlanMode =
  | PlansPlanMode.REBALANCE
  | PlansPlanMode.ALLOCATION
  | PlansPlanMode.IDEAL_PORTFOLIO

type PlansListErrorStateProps = {
  planMode: PlansListErrorStatePlanMode
  onRetry: () => void
}

const PLANS_LIST_ERROR_STATE_CONFIG: Record<
  PlansListErrorStatePlanMode,
  { description: string }
> = {
  [PlansPlanMode.REBALANCE]: {
    description: "We couldn't load your rebalance plan right now."
  },
  [PlansPlanMode.ALLOCATION]: {
    description: "We couldn't load your allocation plan right now."
  },
  [PlansPlanMode.IDEAL_PORTFOLIO]: {
    description: "We couldn't load your ideal portfolio right now."
  }
}

export function PlansListErrorState({ planMode, onRetry }: PlansListErrorStateProps) {
  const errorStateConfig = PLANS_LIST_ERROR_STATE_CONFIG[planMode]

  return (
    <PlansErrorStateLayout className="bg-white">
      <PlansListErrorStateIllustration planMode={planMode} />

      <div className="flex flex-col gap-3 items-center">
        <div className="text-[22px] font-[500] leading-[130%] text-[#232323]">Something went wrong</div>
        <PlansErrorStateDescription message={errorStateConfig.description} />
        <PrimaryButton
          label="Retry"
          onClick={() => {
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: PlansLumpsumListScreenByPlanMode?.[planMode],
                eventId: PlansLumpsumListEventIds.ListRetry,
                buttonLabel: 'Retry'
              }
            })
            onRetry()
          }}
          primaryButtonClassName="!h-10 !w-[140px] !mt-5"
        />
      </div>
    </PlansErrorStateLayout>
  )
}

const PLANS_LIST_ERROR_ILLUSTRATION_ACCENT: Record<PlansListErrorStatePlanMode, string> = {
  [PlansPlanMode.REBALANCE]: '#cc5c2d',
  [PlansPlanMode.ALLOCATION]: '#7048AC',
  [PlansPlanMode.IDEAL_PORTFOLIO]: '#2e8ae5'
}

export function PlansListErrorStateIllustration({
  planMode
}: {
  planMode: PlansListErrorStatePlanMode
}) {
  const accentColor = PLANS_LIST_ERROR_ILLUSTRATION_ACCENT[planMode]

  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="block size-16 shrink-0"
    >
      <rect width="64" height="64" rx="16" fill={accentColor} fillOpacity="0.12" />
      <circle cx="32" cy="32" r="18" stroke={accentColor} strokeWidth="3" />
      <path d="M32 20v14" stroke={accentColor} strokeWidth="3.5" strokeLinecap="round" />
      <circle cx="32" cy="41.5" r="2.75" fill={accentColor} />
    </svg>
  )
}

export function PlansErrorStateDescription({ message }: { message: string }) {
  return (
    <div className="max-w-[219px] text-center text-[13px] font-[400] leading-[140%] text-[#6A6E6E]">
      {message}{' '}
      Please try again.
    </div>
  )
}
